import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams, useHistory, Route, Switch } from 'react-router-dom';
import { ChatList, MessageContainer } from '../../components/messages';
import websocketService from '../../services/websocketService';
import { useGetUnreadCountQuery } from '../../redux/apis/endpoints/messageApi';
import { useGetFulfillmentGroupDetailsQuery, useGetOrderDetailsQuery } from '../../redux/apis/endpoints/orderApi';
import { useGetProductQuery } from '../../redux/apis';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import BackButton from '../../components/common/BackButton';

const MessagePage = () => {
  const [activeChatListUserId, setActiveChatListUserId] = useState(null);
  const [selectedChatObject, setSelectedChatObject] = useState(null);
  const [chatObjectForContainer, setChatObjectForContainer] = useState(null);
  const [mobileView, setMobileView] = useState(false);
  const [showMessageArea, setShowMessageArea] = useState(false);
  const [wsConnectionFailed, setWsConnectionFailed] = useState(false);
  const location = useLocation();
  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { isAuthenticated, token, user } = useSelector(state => state.auth);
  const { data: unreadCount, isLoading: isLoadingUnreadCount } = useGetUnreadCountQuery(undefined, {
    skip: !isAuthenticated
  });
  
  // Get parameters from URL
  const { fulfillmentGroupId: urlFulfillmentGroupId, orderId: urlOrderId, productId: urlProductId } = params;
  
  // Fetch product details if productId is in the URL
  const { data: productDetails, isLoading: isLoadingProductDetails } = useGetProductQuery(urlProductId, {
    skip: !urlProductId || !user,
  });
  
  // Handle fulfillment group case
  const { data: fulfillmentGroupDetails, isLoading: isLoadingFgDetailsForList } = useGetFulfillmentGroupDetailsQuery(
    urlFulfillmentGroupId,
    {
      skip: !urlFulfillmentGroupId || !user,
    }
  );
  
  // Handle order case
  const { data: orderDetails, isLoading: isLoadingOrderDetails } = useGetOrderDetailsQuery(
    urlOrderId,
    {
      skip: !urlOrderId || !user,
    }
  );
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  useEffect(() => {
    const handleResize = () => {
      setMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    if (isAuthenticated && token) {
      websocketService.connect(token).catch((error) => {
        console.error('WebSocket connection failed:', error);
        setWsConnectionFailed(true);
      });
      
      return () => {
        websocketService.disconnect();
      };
    }
  }, [isAuthenticated, token]);
  
  useEffect(() => {
    let newChatObjectForContainer = null;
    let newActiveChatListUserId = null;

    // Priority 1: Product context from URL
    if (urlProductId) {
      if (productDetails && user) {
        const seller = productDetails.merchant || productDetails.users_permissions_user;
        if (seller) {
          newActiveChatListUserId = String(seller.id);
          newChatObjectForContainer = {
            id: newActiveChatListUserId,
            username: seller.username || 'Seller',
            avatar: seller.avatar?.url,
            productContext: productDetails,
          };
        } else {
          console.error("Seller information missing on product:", productDetails);
        }
      }
    }
    // Priority 2: Order context from URL
    else if (urlOrderId) {
      if (orderDetails && user) {
        let otherUserId;
        let otherUserName;
        let otherUserAvatar;
        const isSeller = orderDetails.merchants?.some(m => String(m.id) === String(user.id));
        const isBuyer = String(orderDetails.users_permissions_user?.id) === String(user.id);

        if (isSeller) {
          otherUserId = orderDetails.users_permissions_user?.id;
          otherUserName = orderDetails.users_permissions_user?.username;
          otherUserAvatar = orderDetails.users_permissions_user?.avatar?.url;
        } else if (isBuyer) {
          otherUserId = orderDetails.merchants?.[0]?.id;
          otherUserName = orderDetails.merchants?.[0]?.username || 'Merchant';
          otherUserAvatar = orderDetails.merchants?.[0]?.avatar?.url;
        }
        if (otherUserId) {
          newActiveChatListUserId = String(otherUserId);
          newChatObjectForContainer = { id: newActiveChatListUserId, username: otherUserName || 'User', avatar: otherUserAvatar };
        }
      }
    }
    // Priority 3: Fulfillment Group context from URL
    else if (urlFulfillmentGroupId) {
      if (fulfillmentGroupDetails && user) {
        let otherUserId;
        let otherUserName;
        let otherUserAvatar;
        const isSeller = String(fulfillmentGroupDetails.merchant?.id) === String(user.id);
        const isBuyer = String(fulfillmentGroupDetails.customer?.id) === String(user.id);

        if (isSeller) {
          otherUserId = fulfillmentGroupDetails.customer?.id;
          otherUserName = fulfillmentGroupDetails.customer?.username;
          otherUserAvatar = fulfillmentGroupDetails.customer?.avatar?.url;
        } else if (isBuyer) {
          otherUserId = fulfillmentGroupDetails.merchant?.id;
          otherUserName = fulfillmentGroupDetails.merchant?.username;
          otherUserAvatar = fulfillmentGroupDetails.merchant?.avatar?.url;
        }
        if (otherUserId) {
          newActiveChatListUserId = String(otherUserId);
          newChatObjectForContainer = { id: newActiveChatListUserId, username: otherUserName || 'User', avatar: otherUserAvatar };
        }
      }
    }
    // Priority 4: Chat selected via click (passed through location.state by older logic or if selectedChatObject is set)
    // or direct navigation to /message/:chatId
    else if (params.chatId) {
      newActiveChatListUserId = String(params.chatId);
      // If selectedChatObject is already set and matches the URL, use it
      if (selectedChatObject && String(selectedChatObject.id) === newActiveChatListUserId) {
        newChatObjectForContainer = selectedChatObject;
      } else {
        // Fallback: construct a minimal object if no matching selectedChatObject is found.
        // Ideally, ChatList data would be used here to find the full object.
        // This typically happens on a direct load to /message/:chatId or if selectedChatObject is stale.
        newChatObjectForContainer = { id: newActiveChatListUserId, username: 'Chat User', avatar: null }; // Placeholder
        // To make this better, one might try to find the chat from chatListFromQuery here if available.
      }
    }
    // Priority 5: Chat initiated via location.state (e.g., "Message Seller" button)
    // This is often a precursor to params.chatId being set by history.push in handleSelectChat
    else if (location.state?.merchantId) {
        const merchantIdStr = String(location.state.merchantId);
        newActiveChatListUserId = merchantIdStr;
        newChatObjectForContainer = {
            id: merchantIdStr,
            username: location.state.merchantName || 'User',
            product: location.state.product, // If product context from "Message Seller"
            avatar: location.state.avatarUrl
        };
        // setSelectedChatObject(newChatObjectForContainer); // This might be redundant if handleSelectChat also does it
    }

    setChatObjectForContainer(newChatObjectForContainer);
    if (newActiveChatListUserId) {
      setActiveChatListUserId(newActiveChatListUserId);
    }

    // Manage mobile view based on whether a chat is active
    if (mobileView) {
      if (newChatObjectForContainer) {
        setShowMessageArea(true);
      } else if (!urlProductId && !urlOrderId && !urlFulfillmentGroupId && !params.chatId && !location.state?.merchantId) {
        // Only hide message area if no context implies a chat should be open
        setShowMessageArea(false);
      }
    }

  }, [
    urlFulfillmentGroupId,
    urlOrderId,
    urlProductId,
    params.chatId, // Listen to changes in URL's chatId
    fulfillmentGroupDetails,
    orderDetails,
    productDetails,
    user,
    location.state,
    mobileView,
    selectedChatObject // Add selectedChatObject as a dependency
    //isLoadingFgDetails, isLoadingOrderDetails, isLoadingProductDetails, // These are for loading states, not direct chat object logic
  ]);
  
  const handleSelectChat = (chat) => {
    const clickedChatId = String(chat.id);

    // If we are in a product-specific chat context via URL
    if (urlProductId && productDetails) {
      const productSeller = productDetails.merchant || productDetails.users_permissions_user;
      const productSellerId = getCleanId(productSeller?.id);

      if (productSellerId === clickedChatId) {
        // Clicked on the same user as current product context
        setActiveChatListUserId(clickedChatId); // Ensure selection
        setSelectedChatObject(chat); // Update selected object
        if (mobileView) setShowMessageArea(true);
        return;
      }
      // Clicked a different user: clear product context by navigating to general chat
      // setSelectedChatObject(chat); // Set before navigating
      // history.push(`/message/${clickedChatId}`); // This will trigger the useEffect
      // return;
    }
    // Simplified logic for general chat selection:
    // 1. Update the selectedChatObject state.
    // 2. Update the activeChatListUserId for highlighting.
    // 3. If the URL doesn't match, navigate. The useEffect will handle setting chatObjectForContainer.

    setSelectedChatObject(chat); // This is the primary state to update
    setActiveChatListUserId(clickedChatId);

    if (String(params.chatId) !== clickedChatId) {
      history.push(`/message/${clickedChatId}`);
    } else {
      // If already on the correct URL, but maybe chatObjectForContainer is not set (e.g. after product context cleared)
      // The useEffect reacting to selectedChatObject change should handle this.
      if (mobileView && !showMessageArea) { // Ensure message area is shown if switching back.
          setShowMessageArea(true);
      }
    }
    
    if (mobileView) {
      setShowMessageArea(true);
    }
  };
  
  const handleBackToList = () => {
    setShowMessageArea(false);
  };
  
  // Helper function (can be moved to utils)
  const getCleanId = (idVal) => {
    const idStr = idVal ? String(idVal).trim() : null;
    return idStr && idStr !== "" ? idStr : null;
  };
  
  if (!isAuthenticated) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-md bg-white rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Sign In Required</h2>
          <p className="text-gray-600 mb-6">Please sign in to access your messages.</p>
          <a 
            href="/login" 
            className="px-6 py-2 bg-brand-teal text-white font-medium rounded-md hover:bg-brand-teal-dark transition-colors"
          >
            Sign In
          </a>
        </div>
      </div>
    );
  }
  
  if (isLoadingUnreadCount || (urlProductId && isLoadingProductDetails)) {
    return <LoadingSpinner />;
  }
  
  const showFulfillmentGroupChat = !!urlFulfillmentGroupId;
  const showOrderChat = !!urlOrderId;
  const showProductChat = !!urlProductId && !!productDetails;
  
  let productContextSellerInfo = null;
  if (showProductChat && productDetails) {
    const seller = productDetails.merchant || productDetails.users_permissions_user;
    if (seller) {
      productContextSellerInfo = {
        id: getCleanId(seller.id),
        username: seller.username || 'Seller',
        avatar: seller.profilePic || null,
        isProductContextPlaceholder: true 
      };
    }
  }
  
  return (
    <div className="container mx-auto px-8 pb-8 pt-8" style={{ height: "calc(100vh - 102px)" }}>
      <div className="flex items-center justify-between mb-4 relative">
        <BackButton />
        <div className="flex-grow text-center">
          <h1 className="text-2xl font-bold text-gray-800">Messages</h1>
        </div>
        <div className="w-16"></div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden" style={{ height: "calc(100% - 60px)" }}>
        {mobileView ? (
          showMessageArea ? (
            <MessageContainer 
              fulfillmentGroupId={showFulfillmentGroupChat ? urlFulfillmentGroupId : null}
              orderId={showOrderChat ? urlOrderId : null}
              productContext={showProductChat ? productDetails : null}
              chat={chatObjectForContainer}
              currentUser={user}
              onBack={handleBackToList}
              className="w-full h-full"
              diagnosticId="MESSAGE_PAGE_MOBILE_CONTAINER"
            />
          ) : (
            <ChatList 
              onSelectChat={handleSelectChat}
              selectedChatId={activeChatListUserId}
              productContextSellerInfo={productContextSellerInfo}
              className="w-full h-full"
            />
          )
        ) : (
          <div className="flex h-full">
            <ChatList 
              onSelectChat={handleSelectChat}
              selectedChatId={activeChatListUserId}
              productContextSellerInfo={productContextSellerInfo}
              className="w-1/3 border-r border-gray-200"
            />
            
            <MessageContainer 
              fulfillmentGroupId={urlFulfillmentGroupId}
              orderId={showOrderChat ? urlOrderId : null}
              productContext={showProductChat ? productDetails : null}
              chat={chatObjectForContainer}
              currentUser={user}
              className="w-2/3"
              diagnosticId="MESSAGE_PAGE_DESKTOP_CONTAINER"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagePage; 